import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classNames from 'classnames';
import isEqual from 'lodash-es/isEqual';
import isObject from 'lodash-es/isObject';
import uniqBy from 'lodash-es/uniqBy';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';
import { PropTypes as PropTypes$1 } from '@panneau/core';

var _excluded = ["value", "clearValue", "options", "isAsync", "disabled", "multiple", "searchable", "creatable", "withoutReset", "noOptionsMessage", "createPrefix", "onCreateOption", "placeholder", "autoSize", "getOptionValue", "getOptionLabel", "loadOptions", "styles", "onChange", "className", "selectClassName", "valueIsOption"];
var propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
  // eslint-disable-next-line react/forbid-prop-types
  clearValue: PropTypes.any,
  options: PropTypes$1.selectOptions,
  isAsync: PropTypes.bool,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  searchable: PropTypes.bool,
  creatable: PropTypes.bool,
  withoutReset: PropTypes.bool,
  noOptionsMessage: PropTypes$1.label,
  createPrefix: PropTypes.string,
  onCreateOption: PropTypes.func,
  placeholder: PropTypes$1.label,
  autoSize: PropTypes.bool,
  loadOptions: PropTypes.func,
  getOptionValue: PropTypes.func,
  getOptionLabel: PropTypes.func,
  valueIsOption: PropTypes.bool,
  styles: PropTypes.shape({}),
  onChange: PropTypes.func,
  className: PropTypes.string,
  selectClassName: PropTypes.string
};
var defaultProps = {
  value: null,
  clearValue: null,
  options: [],
  isAsync: false,
  disabled: false,
  multiple: false,
  searchable: true,
  creatable: false,
  withoutReset: false,
  noOptionsMessage: /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "pZytAx",
    defaultMessage: [{
      "type": 0,
      "value": "No result"
    }]
  }),
  createPrefix: 'Create',
  onCreateOption: null,
  placeholder: /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "6TuTuq",
    defaultMessage: [{
      "type": 0,
      "value": "Choose an option"
    }]
  }),
  autoSize: false,
  getOptionValue: null,
  getOptionLabel: null,
  loadOptions: null,
  valueIsOption: false,
  styles: null,
  onChange: null,
  className: null,
  selectClassName: null
};
var SelectElement = function SelectElement(_ref) {
  var value = _ref.value,
    clearValue = _ref.clearValue,
    options = _ref.options,
    isAsync = _ref.isAsync,
    disabled = _ref.disabled,
    multiple = _ref.multiple,
    searchable = _ref.searchable,
    creatable = _ref.creatable,
    withoutReset = _ref.withoutReset,
    _noOptionsMessage = _ref.noOptionsMessage,
    createPrefix = _ref.createPrefix,
    onCreateOption = _ref.onCreateOption,
    placeholder = _ref.placeholder,
    autoSize = _ref.autoSize,
    getOptionValue = _ref.getOptionValue,
    getOptionLabel = _ref.getOptionLabel,
    loadOptions = _ref.loadOptions,
    styles = _ref.styles,
    onChange = _ref.onChange,
    className = _ref.className,
    selectClassName = _ref.selectClassName,
    valueIsOption = _ref.valueIsOption,
    props = _objectWithoutProperties(_ref, _excluded);
  var shouldConvertValue = !valueIsOption;
  var safeOptions = useMemo(function () {
    return options !== null ? (options || []).map(function (it) {
      return !isObject(it) ? {
        value: it,
        label: it
      } : it;
    }).filter(function (it) {
      return it !== null;
    }) : null;
  }, [options]);
  var _useState = useState(safeOptions || []),
    _useState2 = _slicedToArray(_useState, 2),
    allOptions = _useState2[0],
    setAllOptions = _useState2[1];
  var addOptions = useCallback(function (newOptions) {
    return setAllOptions(uniqBy([].concat(_toConsumableArray(allOptions), _toConsumableArray(newOptions)), function (option) {
      return getOptionValue !== null ? getOptionValue(option) : option.value;
    }));
  }, [allOptions, getOptionValue]);
  var finalLoadOptions = useMemo(function () {
    return loadOptions !== null && shouldConvertValue ? function (searchValue) {
      return loadOptions(searchValue).then(function (newOptions) {
        addOptions(newOptions);
        return newOptions;
      });
    } : loadOptions;
  }, [loadOptions, shouldConvertValue, addOptions]);
  useEffect(function () {
    if (shouldConvertValue) {
      addOptions(safeOptions);
    }
  }, [shouldConvertValue, safeOptions]);
  var onChangeOption = useCallback(function (newValue) {
    if (shouldConvertValue && newValue !== null) {
      addOptions(multiple ? newValue : [newValue]);
    }
    if (onChange === null) {
      return;
    }
    if (multiple && shouldConvertValue && newValue !== null) {
      onChange(newValue.map(function (newValueItem) {
        return getOptionValue !== null ? getOptionValue(newValueItem) : newValueItem.value;
      }));
    } else if (shouldConvertValue && newValue !== null) {
      onChange(getOptionValue !== null ? getOptionValue(newValue) : newValue.value);
    } else {
      onChange(newValue);
    }
  }, [onChange, multiple, shouldConvertValue, getOptionValue, addOptions]);
  var finalValue = useMemo(function () {
    if (!shouldConvertValue || value === null) {
      return value;
    }
    function findOption(val) {
      return allOptions.find(function (opt) {
        return isEqual(val, getOptionValue !== null ? getOptionValue(opt) : opt.value);
      }) || null;
    }
    return multiple ? (value || []).map(findOption).filter(function (it) {
      return it !== null;
    }) : findOption(value);
  }, [value, allOptions, getOptionValue, multiple]);
  var minWidth = useMemo(function () {
    return (safeOptions || []).reduce(function (width, _ref2) {
      var _ref2$label = _ref2.label,
        label = _ref2$label === void 0 ? null : _ref2$label;
      return Math.max(width, (label !== null ? label.length : 0) * 8 + 100);
    }, 100);
  }, [safeOptions]);
  var SelectComponent = Select;
  if (isAsync && creatable) {
    SelectComponent = AsyncCreatableSelect;
  } else if (isAsync) {
    SelectComponent = AsyncSelect;
  } else if (creatable) {
    SelectComponent = CreatableSelect;
  }

  // console.log('select', isAsync, creatable, SelectComponent, onCreateOption);

  return /*#__PURE__*/React.createElement("div", {
    className: classNames(['position-relative', _defineProperty({}, className, className !== null)])
  }, /*#__PURE__*/React.createElement(SelectComponent, Object.assign({}, props, getOptionValue !== null ? {
    getOptionValue: getOptionValue
  } : null, getOptionLabel !== null ? {
    getOptionLabel: getOptionLabel
  } : null, creatable ? {
    onCreateOption: onCreateOption,
    formatCreateLabel: function formatCreateLabel(newLabel) {
      return "".concat(createPrefix || 'Create', " ").concat(newLabel);
    }
  } : null, safeOptions !== null ? {
    options: safeOptions
  } : null, finalLoadOptions !== null ? {
    loadOptions: finalLoadOptions
  } : null, {
    className: selectClassName !== null ? selectClassName : null
    // menuPortalTarget={document.body}
    ,
    styles: _objectSpread({
      container: function container(base) {
        return _objectSpread(_objectSpread(_objectSpread({}, base), autoSize ? {
          minWidth: minWidth
        } : null), {}, {
          maxWidth: '100%'
          // ':focus': {
          //     ...base[':focus'],
          //     border: '2px solid black',
          // },
        });
      },
      menuPortal: function menuPortal(base) {
        return _objectSpread(_objectSpread({}, base), {}, {
          zIndex: 10
        });
      },
      placeholder: function placeholder(base) {
        return _objectSpread(_objectSpread({}, base), {}, {
          whiteSpace: 'normal'
        }, autoSize ? {
          whiteSpace: 'nowrap'
        } : null);
      },
      singleValue: function singleValue(base) {
        return _objectSpread(_objectSpread({}, base), {}, {
          color: 'var(--bs-body-color)'
        });
      },
      multiValueRemove: function multiValueRemove(base, _ref4) {
        var isDisabled = _ref4.isDisabled;
        return _objectSpread(_objectSpread({}, base), {}, {
          color: 'var(--bs-dark)'
        }, isDisabled ? {
          visibility: 'hidden',
          width: '0',
          paddingLeft: '0'
        } : {});
      },
      input: function input(base, _ref5) {
        var isDisabled = _ref5.isDisabled;
        return _objectSpread(_objectSpread({}, base), {}, {
          color: 'var(--bs-body-color)',
          cursor: isDisabled ? 'not-allowed' : 'pointer'
        });
      },
      control: function control(base, _ref6) {
        var isDisabled = _ref6.isDisabled,
          isFocused = _ref6.isFocused;
        return _objectSpread(_objectSpread({}, base), {}, {
          color: 'var(--bs-body-color)',
          backgroundColor: 'var(--bs-input-color)',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          borderColor: isFocused ? 'rgba(var(--bs-primary-rgb), 0.25)' : 'hsl(0, 0%, 80%)',
          boxShadow: isFocused ? '0 0 0 1px rgba(var(--bs-primary-rgb), 0.25)' : 'none',
          '&:hover': {
            borderColor: 'rgba(var(--bs-primary-rgb), 0.25)',
            boxShadow: '0 0 0 1px rgba(var(--bs-primary-rgb, 0.25)'
          }
        });
      },
      indicators: function indicators(base) {
        return _objectSpread(_objectSpread({}, base), {}, {
          color: 'var(--bs-body-bg)',
          backgroundColor: 'var(--bs-body-color)'
        });
      },
      menu: function menu(base) {
        return _objectSpread(_objectSpread({}, base), {}, {
          backgroundColor: 'var(--bs-body-color)',
          zIndex: 1001
        });
      },
      menuList: function menuList(base) {
        return _objectSpread(_objectSpread({}, base), {}, {
          backgroundColor: 'var(--bs-body-bg)',
          border: '1px solid var(--bs-border-color)',
          borderRadius: '4px'
        });
      },
      option: function option(base, _ref7) {
        var isDisabled = _ref7.isDisabled;
        return _objectSpread(_objectSpread({}, base), {}, {
          color: 'var(--bs-body-color)',
          backgroundColor: 'var(--bs-body-bg)',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          ':active': _objectSpread(_objectSpread({}, base[':active']), {}, {
            color: 'var(--bs-primary)',
            cursor: 'pointer'
          })
        });
      },
      dropdownIndicator: function dropdownIndicator(base) {
        return _objectSpread(_objectSpread({}, base), {}, {
          color: 'inherit'
        });
      }
    }, styles !== null ? styles : null),
    value: finalValue || null,
    isDisabled: disabled,
    isMulti: multiple,
    isClearable: !withoutReset,
    clearValue: clearValue,
    isSearchable: searchable,
    noOptionsMessage: function noOptionsMessage() {
      return _noOptionsMessage;
    },
    placeholder: placeholder,
    onChange: onChangeOption
  })));
};
SelectElement.propTypes = propTypes;
SelectElement.defaultProps = defaultProps;

export { SelectElement as default };
